import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  Calendar,
  MessageCircle,
  Users,
  Car,
  Accessibility,
  Wifi,
  CreditCard,
  Building
} from 'lucide-react';
import { Card, Button, Input, Textarea, Select } from '../../components/ui';
import { mockClinicInfo } from '../../data/mockData';
import { ContactFormData } from '../../types';
import toast from 'react-hot-toast';

const ContactPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const contactReasons = [
    { value: '', label: 'Select a subject' },
    { value: 'appointment', label: 'Schedule an Appointment' },
    { value: 'insurance', label: 'Insurance Questions' },
    { value: 'billing', label: 'Billing Inquiry' },
    { value: 'medical-records', label: 'Medical Records Request' },
    { value: 'feedback', label: 'Feedback or Complaint' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'emergency', label: 'Medical Emergency' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const facilities = [
    { icon: Car, title: 'Free Parking', description: 'Complimentary parking available' },
    { icon: Accessibility, title: 'Wheelchair Accessible', description: 'Full accessibility throughout' },
    { icon: Wifi, title: 'Free WiFi', description: 'Stay connected during your visit' },
    { icon: CreditCard, title: 'Multiple Payment Options', description: 'Cash, cards, and insurance accepted' }
  ];

  const quickActions = [
    {
      icon: Calendar,
      title: 'Book Appointment',
      description: 'Schedule your visit online',
      action: 'Book Now',
      href: '/booking',
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      icon: Phone,
      title: 'Call Us',
      description: 'Speak with our team',
      action: 'Call Now',
      href: `tel:${mockClinicInfo.phone}`,
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Chat with support',
      action: 'Start Chat',
      href: '#',
      color: 'bg-purple-600 hover:bg-purple-700'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=1920&h=1080&fit=crop&crop=center"
            alt="Contact Our Medical Center"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/60 via-blue-900/70 to-blue-800/65"></div>
        </div>
        
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center space-y-8">
            {/* Contact Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
                Contact ClinicIT Medical Center
              </span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Get in{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  TOUCH
                </span>
            </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                We're here to help with any questions about our services, appointments, or how we can better serve your healthcare needs.
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="pt-6">
              <Link to="/booking">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Immediate Assistance?
            </h2>
            <p className="text-xl text-gray-600">
              Choose the fastest way to get the help you need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickActions.map((action, index) => (
              <Card key={index} className="text-center card-hover" padding="lg">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${action.color.split(' ')[0].replace('hover:', '').replace('bg-', 'bg-').replace('-600', '-100')}`}>
                  <action.icon className={`w-8 h-8 ${action.color.split(' ')[0].replace('bg-', 'text-')}`} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {action.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {action.description}
                </p>
                <a href={action.href}>
                  <Button className={action.color}>
                    {action.action}
                  </Button>
                </a>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Send Us a Message
              </h2>
              
              <Card>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      icon={<Users className="w-4 h-4" />}
                    />
                    
                    <Input
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                      icon={<Mail className="w-4 h-4" />}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Phone Number"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      icon={<Phone className="w-4 h-4" />}
                    />
                    
                    <Select
                      label="Subject"
                      options={contactReasons}
                      value={formData.subject}
                      onChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                      required
                    />
                  </div>
                  
                  <Textarea
                    label="Message"
                    placeholder="Tell us how we can help you..."
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    required
                    rows={5}
                  />
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    className="w-full"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                  Contact Information
                </h2>
                
                <div className="space-y-6">
                  <Card className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Address</h3>
                        <p className="text-gray-600">
                          {mockClinicInfo.address.street}<br />
                          {mockClinicInfo.address.city}, {mockClinicInfo.address.state} {mockClinicInfo.address.zipCode}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                        <Phone className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                        <p className="text-gray-600">
                          <a href={`tel:${mockClinicInfo.phone}`} className="hover:text-green-600">
                            {mockClinicInfo.phone}
                          </a>
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          24/7 Emergency Line Available
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Mail className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                        <p className="text-gray-600">
                          <a href={`mailto:${mockClinicInfo.email}`} className="hover:text-purple-600">
                            {mockClinicInfo.email}
                          </a>
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          We'll respond within 24 hours
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Hours</h3>
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex justify-between">
                            <span>Monday - Friday:</span>
                            <span>8:00 AM - 6:00 PM</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Saturday:</span>
                            <span>9:00 AM - 2:00 PM</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Sunday:</span>
                            <span>Closed</span>
                          </div>
                          <p className="text-orange-600 mt-2 font-medium">
                            Emergency services available 24/7
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>

              {/* Facilities */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Facility Features
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {facilities.map((facility, index) => (
                    <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                      <facility.icon className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{facility.title}</p>
                        <p className="text-xs text-gray-500">{facility.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Find Us
            </h2>
            <p className="text-xl text-gray-600">
              Conveniently located in the heart of San Francisco
            </p>
          </div>
          
          <Card className="overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 bg-gray-200">
              {/* This would typically contain an embedded Google Map */}
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Interactive Map
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {mockClinicInfo.address.street}, {mockClinicInfo.address.city}
                  </p>
                  <Button variant="outline">
                    <MapPin className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Need directions or transportation assistance?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline">
                <Car className="w-4 h-4 mr-2" />
                Parking Information
              </Button>
              <Button variant="outline">
                <MapPin className="w-4 h-4 mr-2" />
                Public Transportation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="py-12 bg-red-50 border-t-4 border-red-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-xl font-bold text-red-800 mb-2">
              Medical Emergency?
            </h3>
            <p className="text-red-700 mb-4">
              If you're experiencing a medical emergency, don't wait. Call 911 or go to your nearest emergency room immediately.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-red-600 hover:bg-red-700">
                <Phone className="w-4 h-4 mr-2" />
                Call 911
              </Button>
              <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                Emergency Room Locations
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;